'use client'

import { ArticleInList } from '@/components/frontend/Layouts/ArticleInList'
import { JSX } from 'react'
import { Post, User } from '@/payload-types'
import { useRouter } from 'next/navigation'
import { ChevronRight } from 'lucide-react'
import { Locale } from '@/providers/Locale/types'
import { AuthorCard } from '@/components/frontend/AuthorCard'
import { i18n } from './i18n'
import { GoogleTagManager } from '@/providers/GoogleTagManager'

type Props = {
    author: User
    posts: Post[]
    locale: Locale
    nextPage?: number
    hasNextPage?: boolean
}

export default function PageClient({ author, posts, locale, nextPage, hasNextPage }: Props): JSX.Element {
    const router = useRouter()

    return (
        <div className='flex w-full justify-center max-w-[1336px] pt-10 m-auto'>
            <GoogleTagManager
                event='page_view'
                gtmData={{
                    site_section: 'author',
                    content_author: author.name || 'Unknown Author',
                }}
            />
            <div className='grid grid-cols-1 lg:grid-cols-3 w-full'>
                <main className='col-span-3 lg:col-span-2 border-r border-gray-200'>
                    <div className='p-10 pr-[18px]'>
                        {posts.length === 0 ? (
                            <p className='text-gray-500'>{i18n[locale].userHasNoPosts}</p>
                        ) : (
                            posts.map((post) => (
                                <ArticleInList
                                    key={post.id}
                                    post={post}
                                    variant='large'
                                    media={{
                                        media: post.heroImage,
                                    }}
                                    classes={{
                                        article: 'border-b border-gray-200 pb-8 last-of-type:border-0',
                                    }}
                                    locale={locale}
                                />
                            ))
                        )}
                        {hasNextPage && nextPage ? (
                            <div className='w-full flex justify-center mt-5 mb-10'>
                                <button
                                    onClick={() => {
                                        router.push(`?page=${nextPage}`)
                                    }}
                                    className='px-8 py-2 bg-white text-stone-500 hover:text-zinc-800 rounded-3xl transition-all duration-200 border border-zinc-800'
                                >
                                    {i18n[locale].moreStories} <ChevronRight className='inline-block stroke-1' />
                                </button>
                            </div>
                        ) : null}
                    </div>
                </main>
                <aside className='pl-8 w-full'>
                    <AuthorCard author={author} />
                </aside>
            </div>
        </div>
    )
}
